// Host-only gallery settings menu — rename event, change icon, delete event.
// Mounted in the gallery header for the album owner. Hidden for guests.
import React, { useEffect, useRef, useState } from "react";
import { Settings, Pencil, Image as ImgIcon, Trash2, Check, X as XIcon, AlertTriangle, Lock, LockOpen } from "lucide-react";
import { toast } from "sonner";
import { Albums } from "../lib/api";
import { setHostPasscodePlain, getHostPasscodePlain } from "../lib/api";
import { EVENT_ICONS, getIconConfig } from "../lib/eventIcons";

export default function GallerySettingsMenu({ album, onUpdated, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(null); // 'rename' | 'icon' | 'passcode' | 'delete' | null
  const [name, setName] = useState(album.name || "");
  const [icon, setIcon] = useState(album.icon || "camera");
  const [confirmText, setConfirmText] = useState("");
  const [passcode, setPasscode] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    setName(album.name || "");
    setIcon(album.icon || "camera");
  }, [album]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const close = () => { setOpen(false); setMode(null); setConfirmText(""); setPasscode(""); };

  const doRename = async () => {
    const next = name.trim();
    if (!next || next === album.name) { close(); return; }
    setBusy(true);
    try {
      const updated = await Albums.patch(album.slug, { name: next });
      toast.success("Renamed");
      onUpdated && onUpdated(updated);
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't rename");
    } finally { setBusy(false); }
  };

  const doIcon = async (newIcon) => {
    if (newIcon === album.icon) { close(); return; }
    setBusy(true);
    try {
      const updated = await Albums.patch(album.slug, { icon: newIcon });
      toast.success("Icon changed");
      onUpdated && onUpdated(updated);
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't change icon");
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (confirmText.trim().toLowerCase() !== "delete") return;
    setBusy(true);
    try {
      await Albums.remove(album.slug);
      toast.success("Event deleted");
      onDeleted && onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete");
      setBusy(false);
    }
  };

  const doSetPasscode = async () => {
    const p = passcode.trim();
    if (!p) return;
    setBusy(true);
    try {
      const updated = await Albums.patch(album.slug, { passcode: p });
      setHostPasscodePlain(album.slug, p); // remember on host's device for Share text
      toast.success(album.requires_passcode ? "Passcode changed" : "Album locked");
      onUpdated && onUpdated(updated);
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save passcode");
    } finally { setBusy(false); }
  };

  const doRemovePasscode = async () => {
    setBusy(true);
    try {
      // "" means remove (backend convention)
      const updated = await Albums.patch(album.slug, { passcode: "" });
      setHostPasscodePlain(album.slug, null);
      toast.success("Passcode removed — album is now public");
      onUpdated && onUpdated(updated);
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove passcode");
    } finally { setBusy(false); }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="meye-chip"
        style={{ padding: "8px 12px", background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.12)" }}
        data-testid="gallery-settings-btn"
        title="Event settings"
        aria-label="Event settings"
      >
        <Settings size={14} />
        <span className="hidden sm:inline">Settings</span>
      </button>

      {open && !mode && (
        <div
          className="absolute right-0 mt-2 w-56 rounded-xl p-1.5 z-30"
          style={{
            background: "rgba(15,12,28,0.96)",
            border: "1px solid rgba(127,75,255,0.30)",
            boxShadow: "0 20px 50px -10px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04) inset",
            backdropFilter: "blur(20px)",
          }}
          data-testid="gallery-settings-menu"
        >
          <MenuItem icon={<Pencil size={14} />} label="Rename event" onClick={() => setMode("rename")} testid="settings-rename" />
          <MenuItem icon={<ImgIcon size={14} />} label="Change icon" onClick={() => setMode("icon")} testid="settings-icon" />
          <MenuItem
            icon={album.requires_passcode ? <Lock size={14} /> : <LockOpen size={14} />}
            label={album.requires_passcode ? "Change passcode" : "Set passcode"}
            onClick={() => setMode("passcode")}
            testid="settings-passcode"
          />
          <div style={{ height: 1, background: "rgba(255,255,255,0.06)", margin: "4px 0" }} />
          <MenuItem icon={<Trash2 size={14} />} label="Delete event" danger onClick={() => setMode("delete")} testid="settings-delete" />
        </div>
      )}

      {open && mode === "rename" && (
        <div
          className="absolute right-0 mt-2 w-72 rounded-xl p-4 z-30"
          style={{ background: "rgba(15,12,28,0.96)", border: "1px solid rgba(127,75,255,0.30)", backdropFilter: "blur(20px)" }}
          data-testid="settings-rename-form"
        >
          <div className="text-sm font-semibold mb-2">Rename event</div>
          <input
            autoFocus
            className="meye-input"
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 80))}
            maxLength={80}
            data-testid="settings-rename-input"
            onKeyDown={(e) => { if (e.key === "Enter") doRename(); if (e.key === "Escape") close(); }}
          />
          <div className="flex gap-2 mt-3">
            <button onClick={close} className="meye-chip flex-1 justify-center" data-testid="settings-rename-cancel">Cancel</button>
            <button onClick={doRename} disabled={busy || !name.trim()} className="cta-pill flex-1" style={{ opacity: !name.trim() ? 0.5 : 1 }} data-testid="settings-rename-save">
              {busy ? <div className="meye-spinner"/> : <><Check size={14}/> Save</>}
            </button>
          </div>
        </div>
      )}

      {open && mode === "icon" && (
        <div
          className="absolute right-0 mt-2 w-80 rounded-xl p-4 z-30"
          style={{ background: "rgba(15,12,28,0.96)", border: "1px solid rgba(127,75,255,0.30)", backdropFilter: "blur(20px)" }}
          data-testid="settings-icon-picker"
        >
          <div className="text-sm font-semibold mb-3">Pick an icon</div>
          <div className="grid grid-cols-5 gap-2">
            {EVENT_ICONS.map((e) => {
              const active = e.key === icon;
              const cfg = getIconConfig(e.key);
              const Icon = cfg.Icon;
              return (
                <button
                  key={e.key}
                  onClick={() => { setIcon(e.key); doIcon(e.key); }}
                  className="rounded-lg p-2 flex flex-col items-center gap-1 transition"
                  style={{
                    background: active ? "linear-gradient(135deg, rgba(127,75,255,0.30), rgba(34,226,255,0.18))" : "rgba(255,255,255,0.04)",
                    border: `1px solid ${active ? "#7F4BFF" : "rgba(255,255,255,0.10)"}`,
                  }}
                  data-testid={`settings-icon-${e.key}`}
                >
                  {Icon && <Icon size={18} color={active ? "#fff" : "#C9B6FF"} />}
                  <div className="text-[9px]" style={{ color: active ? "#fff" : "rgba(255,255,255,0.6)" }}>{e.label || e.key}</div>
                </button>
              );
            })}
          </div>
          <button onClick={close} className="meye-chip w-full justify-center mt-3" data-testid="settings-icon-close">
            <XIcon size={12}/> Close
          </button>
        </div>
      )}

      {open && mode === "passcode" && (
        <div
          className="absolute right-0 mt-2 w-80 rounded-xl p-4 z-30"
          style={{ background: "rgba(15,12,28,0.96)", border: "1px solid rgba(127,75,255,0.30)", backdropFilter: "blur(20px)" }}
          data-testid="settings-passcode-form"
        >
          <div className="flex items-center gap-2 mb-1">
            <Lock size={14} color="#C9B6FF" />
            <div className="text-sm font-semibold">
              {album.requires_passcode ? "Change passcode" : "Set a passcode"}
            </div>
          </div>
          <div className="text-[12px] mb-3" style={{ color: "rgba(255,255,255,0.6)" }}>
            {album.requires_passcode
              ? "Guests will need the new code to view or upload."
              : "Only people with the code can view or upload to this album."}
          </div>
          {album.requires_passcode && (album.host_passcode || getHostPasscodePlain(album.slug)) && (
            <div
              className="text-[12px] mb-2 rounded-lg px-2.5 py-1.5"
              style={{ background: "rgba(255,211,0,0.08)", border: "1px solid rgba(255,211,0,0.3)", color: "#FFE373" }}
              data-testid="settings-passcode-current"
            >
              Current passcode: <span className="font-semibold">{album.host_passcode || getHostPasscodePlain(album.slug)}</span>
            </div>
          )}
          <input
            autoFocus
            type="text"
            className="meye-input"
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            placeholder="e.g. wedding2026"
            data-testid="settings-passcode-input"
            onKeyDown={(e) => { if (e.key === "Enter") doSetPasscode(); if (e.key === "Escape") close(); }}
            autoComplete="off"
          />
          <div className="flex gap-2 mt-3">
            <button onClick={close} className="meye-chip flex-1 justify-center" data-testid="settings-passcode-cancel">Cancel</button>
            <button
              onClick={doSetPasscode}
              disabled={busy || !passcode.trim()}
              className="cta-pill flex-1"
              style={{ opacity: !passcode.trim() ? 0.5 : 1 }}
              data-testid="settings-passcode-save"
            >
              {busy ? <div className="meye-spinner"/> : <><Check size={14}/> Save</>}
            </button>
          </div>
          {album.requires_passcode && (
            <button
              onClick={doRemovePasscode}
              disabled={busy}
              className="w-full mt-2 text-[12px] rounded-lg px-3 py-2 flex items-center justify-center gap-1.5"
              style={{ background: "rgba(255,107,107,0.08)", border: "1px solid rgba(255,107,107,0.35)", color: "#FF9B9B" }}
              data-testid="settings-passcode-remove"
            >
              <LockOpen size={12}/> Remove passcode — make album public
            </button>
          )}
        </div>
      )}

      {open && mode === "delete" && (
        <div
          className="absolute right-0 mt-2 w-80 rounded-xl p-4 z-30"
          style={{
            background: "rgba(40,12,18,0.96)",
            border: "1px solid rgba(255,107,107,0.45)",
            backdropFilter: "blur(20px)",
          }}
          data-testid="settings-delete-confirm"
        >
          <div className="flex items-start gap-2 mb-2">
            <AlertTriangle size={16} color="#FF6B6B" style={{ marginTop: 2 }} />
            <div>
              <div className="text-sm font-semibold" style={{ color: "#FF6B6B" }}>Delete this event?</div>
              <div className="text-[12px] mt-1" style={{ color: "rgba(255,255,255,0.7)" }}>
                All photos, videos, and folders will be removed. This cannot be undone.
              </div>
            </div>
          </div>
          <div className="text-[11px] mb-1" style={{ color: "rgba(255,255,255,0.6)" }}>
            Type <span className="font-bold" style={{ color: "#FF6B6B" }}>delete</span> to confirm
          </div>
          <input
            autoFocus
            className="meye-input"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            data-testid="settings-delete-input"
            onKeyDown={(e) => { if (e.key === "Escape") close(); }}
          />
          <div className="flex gap-2 mt-3">
            <button onClick={close} className="meye-chip flex-1 justify-center" data-testid="settings-delete-cancel">Cancel</button>
            <button
              onClick={doDelete}
              disabled={busy || confirmText.trim().toLowerCase() !== "delete"}
              className="meye-chip flex-1 justify-center"
              style={{
                background: confirmText.trim().toLowerCase() === "delete" ? "rgba(255,107,107,0.20)" : "rgba(255,107,107,0.08)",
                borderColor: "rgba(255,107,107,0.55)",
                color: "#FF6B6B",
                opacity: confirmText.trim().toLowerCase() === "delete" ? 1 : 0.5,
              }}
              data-testid="settings-delete-confirm-btn"
            >
              {busy ? <div className="meye-spinner"/> : <><Trash2 size={14}/> Delete forever</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const MenuItem = ({ icon, label, onClick, danger, testid }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-sm transition hover:bg-white/5"
    style={{ color: danger ? "#FF6B6B" : "#fff" }}
    data-testid={testid}
  >
    {icon}
    <span>{label}</span>
  </button>
);
